import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { adminApi } from '../services/api';
import { isNonSGTBrowser, formatDateSGTLabelled } from '../utils/dateUtils';
import InvoicePreview from './InvoicePreview';
import PaymentAdjustmentsList from './PaymentAdjustmentsList';
import RecurringPaymentManager from './RecurringPaymentManager';
import BulkExportModal from './BulkExportModal';

import AuditTrail from './AuditTrail';
import EndorsementStatistics from './EndorsementStatistics';
import TutorDetailModal from './TutorDetailModal';
import TutorEarningsTrends from './TutorEarningsTrends';
import DisputeResolutionModal from './DisputeResolutionModal';
import ProfitSharingModal from './ProfitSharingModal';
import MonthYearNavigator from './MonthYearNavigator';
import { saveMonthYear, loadMonthYear } from '../utils/monthYearPersistence';
import '../assets/InvoiceManagement.css';
import '../assets/PageHeader.css';
import '../assets/SharedTabs.css';

// Using standalone InvoicePreview component
const InvoicePreviewOld = ({ invoiceType, recipient, month, year, invoiceData }) => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const formatDate = (dateString) => {
    return formatDateSGTLabelled(dateString);
  };

  const formatCurrency = (amount) => {
    return `$${amount.toFixed(2)}`;
  };

  return (
    <div className="invoice-preview-content">
      {/* Invoice Header */}
      <div className="invoice-preview-header">
        <div className="invoice-title">
          <h2>{invoiceType === 'tutor' ? 'Tutor Invoice' : 'Tutee Invoice'}</h2>
          <p className="invoice-period">{months[month - 1]} {year}</p>
        </div>
        <div className="invoice-meta">
          <p><strong>Generated:</strong> {new Date().toLocaleDateString('en-SG', { timeZone: 'Asia/Singapore' })}</p>
        </div>
      </div>

      {/* Recipient Info */}
      <div className="invoice-recipient">
        <h3>Bill To:</h3>
        <p className="recipient-name">{recipient.name}</p>
        {recipient.email && <p className="recipient-email">{recipient.email}</p>}
      </div>

      {/* Lessons Table */}
      {invoiceData.lessons && invoiceData.lessons.length > 0 ? (
        <div className="invoice-section">
          <h3>Lessons {invoiceType === 'tutor' ? 'Taught' : 'Received'}</h3>
          <table className="invoice-table">
            <thead>
              <tr>
                <th className={isNonSGTBrowser() ? 'sgt-tz-col-header' : ''}>Date</th>
                <th>{invoiceType === 'tutor' ? 'Tutee' : 'Tutor'}</th>
                <th>Subject</th>
                <th>Duration</th>
                <th>Rate</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoiceData.lessons.map((lesson, index) => {
                const hours = lesson.duration / 60;
                const rate = invoiceType === 'tutor' ? lesson.tutorRate : lesson.rate;
                const amount = rate * hours;
                
                return (
                  <tr key={index}>
                    <td>{formatDate(lesson.date)}</td>
                    <td>{invoiceType === 'tutor' ? lesson.tutee?.name : lesson.tutor?.name}</td>
                    <td>{lesson.subject}</td>
                    <td>{lesson.duration} min</td>
                    <td>{formatCurrency(rate)}/hr</td>
                    <td>{formatCurrency(amount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="invoice-section">
          <p className="no-lessons">No lessons found for this period</p>
        </div>
      )}

      {/* Payment Adjustments */}
      {invoiceData.adjustments && invoiceData.adjustments.length > 0 && (
        <div className="invoice-section">
          <h3>Payment Adjustments</h3>
          <table className="invoice-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Reason</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoiceData.adjustments.map((adjustment, index) => (
                <tr key={index}>
                  <td>{adjustment.type}</td>
                  <td>{adjustment.reason}</td>
                  <td className={adjustment.amount >= 0 ? 'positive' : 'negative'}>
                    {adjustment.amount >= 0 ? '+' : ''}{formatCurrency(adjustment.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recurring Payments */}
      {invoiceData.recurringPayments && invoiceData.recurringPayments.length > 0 && (
        <div className="invoice-section">
          <h3>Recurring Payments</h3>
          <table className="invoice-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Reason</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoiceData.recurringPayments.map((payment, index) => (
                <tr key={index}>
                  <td>{payment.type}</td>
                  <td>{payment.reason}</td>
                  <td className="positive">+{formatCurrency(payment.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Totals */}
      <div className="invoice-totals">
        <div className="totals-row">
          <span>Subtotal (Lessons):</span>
          <span>{formatCurrency(invoiceData.totals?.lessonTotal || 0)}</span>
        </div>
        {invoiceData.totals?.adjustmentTotal !== 0 && (
          <div className="totals-row">
            <span>Adjustments:</span>
            <span className={invoiceData.totals?.adjustmentTotal >= 0 ? 'positive' : 'negative'}>
              {invoiceData.totals?.adjustmentTotal >= 0 ? '+' : ''}
              {formatCurrency(invoiceData.totals?.adjustmentTotal || 0)}
            </span>
          </div>
        )}
        {invoiceData.totals?.recurringTotal !== 0 && (
          <div className="totals-row">
            <span>Recurring Payments:</span>
            <span className="positive">
              +{formatCurrency(invoiceData.totals?.recurringTotal || 0)}
            </span>
          </div>
        )}
        <div className="totals-row total">
          <span>Total {invoiceType === 'tutor' ? 'Payment' : 'Amount'}:</span>
          <span>{formatCurrency(invoiceData.totals?.finalAmount || 0)}</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="invoice-actions">
        <button className="action-btn secondary">Export PDF</button>
        {/* Email Invoice button removed — invoices are not sent via email. */}
      </div>
    </div>
  );
};

const InvoiceManagement = () => {
  const location = useLocation();
  
  // Tab state — Generate Invoice is the default landing view.
  // Persisted in localStorage so a page refresh restores the last-viewed tab
  // instead of snapping back to Generate Invoice.
  const VALID_TABS = ['generate', 'trends', 'recurring', 'adjustments', 'audit'];
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const saved = localStorage.getItem('invoice_management_active_tab');
      return VALID_TABS.includes(saved) ? saved : 'generate';
    } catch {
      return 'generate';
    }
  });

  // Persist the active tab whenever it changes.
  useEffect(() => {
    try {
      localStorage.setItem('invoice_management_active_tab', activeTab);
    } catch {
      /* ignore storage errors (e.g. private mode) */
    }
  }, [activeTab]);
  
  // Date selection state - load from localStorage or use current month/year
  const savedMonthYear = loadMonthYear('invoice_management');
  const [selectedMonth, setSelectedMonth] = useState(savedMonthYear.month);
  const [selectedYear, setSelectedYear] = useState(savedMonthYear.year);
  
  // Endorsement integration state
  const [tutorSummaries, setTutorSummaries] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'endorsed', 'pending', 'disputed'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Modal states
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [showBulkExportModal, setShowBulkExportModal] = useState(false);

  const [showProfitSharingModal, setShowProfitSharingModal] = useState(false);
  
  // Legacy state for other tabs
  const [recipientType, setRecipientType] = useState('tutor');
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [tutors, setTutors] = useState([]);
  const [tutees, setTutees] = useState([]);
  const [showRecipientDropdown, setShowRecipientDropdown] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);

  // Load tutors and tutees on mount
  useEffect(() => {
    fetchTutorsAndTutees();
  }, []);

  // Load endorsement status when month/year changes
  useEffect(() => {
    if (activeTab === 'generate') {
      const abortController = new AbortController();
      fetchEndorsementStatus(abortController.signal);
      return () => abortController.abort();
    }
  }, [selectedMonth, selectedYear, activeTab]);

  // Handle pre-filled data from navigation state (from Monthly Report)
  useEffect(() => {
    const navigationState = location.state;
    if (navigationState) {
      const { recipientId, recipientType: navRecipientType, month, year } = navigationState;
      
      if (recipientId && navRecipientType && month && year) {
        // Arriving from another screen (e.g. Monthly Report) to generate an
        // invoice — force the Generate tab regardless of the saved tab.
        setActiveTab('generate');
        setRecipientType(navRecipientType);
        setSelectedMonth(month);
        setSelectedYear(year);
        
        // Wait for tutors/tutees to load, then select the recipient
        const checkAndSelectRecipient = setInterval(() => {
          const recipients = navRecipientType === 'tutor' ? tutors : tutees;
          if (recipients.length > 0) {
            const recipient = recipients.find(r => r._id === recipientId);
            if (recipient) {
              setSelectedRecipient(recipient);
              // Auto-generate invoice
              setTimeout(() => {
                handleGenerateInvoice(navRecipientType, recipient, month, year);
              }, 100);
            }
            clearInterval(checkAndSelectRecipient);
          }
        }, 100);
        
        // Clear interval after 5 seconds to prevent infinite loop
        setTimeout(() => clearInterval(checkAndSelectRecipient), 5000);
      }
    }
  }, [tutors, tutees, location.state]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.recipient-selector')) {
        setShowRecipientDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchTutorsAndTutees = async () => {
    try {
      const [tutorsRes, tuteesRes] = await Promise.all([
        adminApi.getTutors(),
        adminApi.getTutees()
      ]);
      setTutors(tutorsRes.data.filter(t => !t.isArchived));
      setTutees(tuteesRes.data.filter(t => !t.isArchived));
    } catch (err) {
      console.error('Failed to load tutors/tutees:', err);
      setError('Failed to load tutors and tutees');
    }
  };

  const fetchEndorsementStatus = async (signal) => {
    setLoading(true);
    setError('');
    
    try {
      const [statusRes, statsRes] = await Promise.all([
        adminApi.getEndorsementStatus(selectedMonth, selectedYear, signal),
        adminApi.getEndorsementStatistics(selectedMonth, selectedYear, signal)
      ]);
      
      // If this request was aborted, don't update state
      if (signal?.aborted) return;
      
      // The response structure is { data: { success: true, data: { endorsed, pending, disputed } } }
      // So we need to access statusRes.data.data
      const responseData = statusRes.data.data || statusRes.data;
      
      // Backend returns { endorsed: [], pending: [], disputed: [] }
      // Flatten into a single array for the frontend
      const { endorsed = [], pending = [], disputed = [] } = responseData;
      const allTutors = [...endorsed, ...pending, ...disputed];
      
      console.log(`Loaded ${allTutors.length} tutors (${endorsed.length} endorsed, ${pending.length} pending, ${disputed.length} disputed)`);
      
      setTutorSummaries(allTutors);
      // Fix: statsRes.data has {success: true, data: {...}}, we need the inner data
      setStatistics(statsRes.data.data || statsRes.data);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to load endorsement status:', err);
      console.error('Error response:', err.response?.data);
      if (!signal?.aborted) {
        setError(err.response?.data?.message || 'Failed to load endorsement status');
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  };

  const refreshEndorsementData = () => {
    fetchEndorsementStatus();
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  // Generate years from 2019 to current year + 5 years
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2019 + 6 }, (_, i) => 2019 + i);

  // Filter tutors by search term and status
  const getFilteredTutors = () => {
    let filtered = tutorSummaries;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(tutor =>
        tutor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tutor.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(tutor => tutor.endorsementStatus === statusFilter);
    }

    return filtered;
  };

  // Group tutors by endorsement status
  const getGroupedTutors = () => {
    const filtered = getFilteredTutors();
    
    const endorsed = filtered.filter(t => t.endorsementStatus === 'endorsed')
      .sort((a, b) => new Date(b.endorsedAt) - new Date(a.endorsedAt));
    
    const pending = filtered.filter(t => t.endorsementStatus === 'pending')
      .sort((a, b) => (b.daysSinceMonthEnd || 0) - (a.daysSinceMonthEnd || 0));
    
    const disputed = filtered.filter(t => t.endorsementStatus === 'disputed')
      .sort((a, b) => (b.disputeCount || 0) - (a.disputeCount || 0));

    return { endorsed, pending, disputed };
  };

  // Filter recipients based on search term
  const getFilteredRecipients = () => {
    const recipients = recipientType === 'tutor' ? tutors : tutees;
    if (!searchTerm) return recipients;
    
    return recipients.filter(r => 
      r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleRecipientSelect = (recipient) => {
    setSelectedRecipient(recipient);
    setSearchTerm(recipient.name);
    setShowRecipientDropdown(false);
    // Clear previous invoice data when changing recipient
    setInvoiceData(null);
    setError('');
  };

  const handleRecipientTypeChange = (type) => {
    setRecipientType(type);
    setSelectedRecipient(null);
    setSearchTerm('');
    setInvoiceData(null);
    setError('');
  };

  const getRecipientLabel = () => {
    if (!selectedRecipient) {
      return `Select ${recipientType === 'tutor' ? 'Tutor' : 'Tutee'}`;
    }
    return selectedRecipient.name;
  };

  const validateInvoiceGeneration = () => {
    if (!selectedRecipient) {
      setError('Please select a recipient');
      return false;
    }

    if (!selectedMonth || selectedMonth < 1 || selectedMonth > 12) {
      setError('Please select a valid month');
      return false;
    }

    if (!selectedYear || selectedYear < 2019) {
      setError('Please select a valid year');
      return false;
    }

    return true;
  };

  const handleGenerateInvoice = async (type = null, recipient = null, month = null, year = null) => {
    // Use provided parameters or fall back to state
    const invoiceType = type || recipientType;
    const invoiceRecipient = recipient || selectedRecipient;
    const invoiceMonth = month || selectedMonth;
    const invoiceYear = year || selectedYear;

    // Validate
    if (!invoiceRecipient) {
      setError('Please select a recipient');
      return;
    }

    setLoading(true);
    setError('');
    setInvoiceData(null);

    try {
      let invoiceData;
      if (invoiceType === 'tutor') {
        response = await adminApi.getTutorInvoice(
          invoiceRecipient._id,
          invoiceMonth,
          invoiceYear
        );
        // Tutor invoice returns { success: true, data: {...} }
        invoiceData = response.data.data;
      } else {
        response = await adminApi.getTuteeInvoiceData(
          invoiceRecipient._id,
          invoiceMonth,
          invoiceYear
        );
        // Tutee invoice returns data directly
        invoiceData = response.data;
      }

      setInvoiceData(invoiceData);
    } catch (err) {
      console.error('Failed to generate invoice:', err);
      
      // Handle different types of errors with user-friendly messages
      let errorMessage = 'Failed to generate invoice. Please try again.';
      
      if (err.response?.status === 404) {
        errorMessage = `No lessons found for ${invoiceRecipient.name} in the selected period.`;
      } else if (err.response?.status === 400) {
        errorMessage = err.response.data?.message || 'Invalid request. Please check the selected period.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error occurred. Please try again later.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Endorsement action handlers
  const handleGenerateInvoiceForTutor = async (tutor) => {
    // Set loading state for the specific tutor
    setLoading(true);
    setError('');
    setInvoiceData(null);

    try {
      // Call the invoice generation API
      const response = await adminApi.getTutorInvoice(
        tutor._id,
        selectedMonth,
        selectedYear
      );

      // Set invoice data to display preview
      setInvoiceData(response.data.data);  // Use the nested data
      setSelectedRecipient(tutor);
      setRecipientType('tutor');

      // Refresh endorsement data to update tutor card status
      refreshEndorsementData();
    } catch (err) {
      console.error('Failed to generate invoice:', err);
      
      // Handle different types of errors with user-friendly messages
      let errorMessage = 'Failed to generate invoice. Please try again.';
      
      if (err.response?.status === 404) {
        errorMessage = `No lessons found for ${tutor.name} in the selected period.`;
      } else if (err.response?.status === 400) {
        errorMessage = err.response.data?.message || 'Invalid request. Please check the selected period.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error occurred. Please try again later.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (tutor) => {
    setSelectedTutor(tutor);
    setShowDetailModal(true);
  };

  const handleSendReminder = async (tutor) => {
    // Set loading state for the specific tutor
    const tutorCard = document.querySelector(`[data-tutor-id="${tutor._id}"]`);
    const reminderButton = tutorCard?.querySelector('.btn-send-reminder');
    
    if (reminderButton) {
      reminderButton.disabled = true;
      reminderButton.textContent = 'Sending...';
    }

    try {
      const response = await adminApi.sendEndorsementReminder(tutor._id, selectedMonth, selectedYear);
      
      // Show success message with more details
      const successMessage = `Reminder sent to ${tutor.name} successfully!`;
      alert(successMessage);
      
      // Refresh endorsement data to update tutor card with reminder timestamp
      refreshEndorsementData();
    } catch (err) {
      console.error('Failed to send reminder:', err);
      
      // Handle different types of errors with user-friendly messages
      let errorMessage = 'Failed to send reminder. Please try again.';
      
      if (err.response?.status === 400) {
        if (err.response.data?.message?.includes('24 hours')) {
          errorMessage = `A reminder was already sent to ${tutor.name} within the last 24 hours. Please wait before sending another reminder.`;
        } else if (err.response.data?.message?.includes('already been endorsed')) {
          errorMessage = `${tutor.name} has already endorsed their monthly summary.`;
        } else {
          errorMessage = err.response.data?.message || errorMessage;
        }
      } else if (err.response?.status === 404) {
        errorMessage = `Tutor ${tutor.name} not found or has no lessons for this period.`;
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error occurred. Please try again later.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      alert(errorMessage);
    } finally {
      // Reset button state
      if (reminderButton) {
        reminderButton.disabled = false;
        reminderButton.textContent = 'Send Reminder';
      }
    }
  };

  const handleSendReminderToAll = async () => {
    const pendingTutors = tutorSummaries.filter(t => t.endorsementStatus === 'pending');
    
    if (pendingTutors.length === 0) {
      alert('No pending tutors to send reminders to.');
      return;
    }

    const confirmed = window.confirm(
      `Send endorsement reminders to all ${pendingTutors.length} pending tutors?\n\n` +
      `Note: Tutors who received a reminder in the last 24 hours will be skipped.`
    );

    if (!confirmed) return;

    setLoading(true);
    let successCount = 0;
    let skippedCount = 0;
    let errorCount = 0;
    const errors = [];

    for (const tutor of pendingTutors) {
      try {
        await adminApi.sendEndorsementReminder(tutor._id, selectedMonth, selectedYear);
        successCount++;
      } catch (err) {
        if (err.response?.status === 400 && err.response.data?.message?.includes('24 hours')) {
          skippedCount++;
        } else {
          errorCount++;
          errors.push(`${tutor.name}: ${err.response?.data?.message || 'Unknown error'}`);
        }
      }
    }

    setLoading(false);

    // Show summary
    let message = `Reminders sent successfully!\n\n`;
    message += `✓ Sent: ${successCount}\n`;
    if (skippedCount > 0) message += `⊘ Skipped (sent within 24h): ${skippedCount}\n`;
    if (errorCount > 0) {
      message += `✗ Failed: ${errorCount}\n\n`;
      message += `Errors:\n${errors.slice(0, 5).join('\n')}`;
      if (errors.length > 5) message += `\n... and ${errors.length - 5} more`;
    }

    alert(message);
    refreshEndorsementData();
  };

  const handleViewDisputes = (tutor) => {
    setSelectedTutor(tutor);
    setShowDisputeModal(true);
  };

  const handleDisputeResolved = () => {
    // Refresh endorsement status after dispute resolution
    fetchEndorsementStatus();
  };

  const handleViewInvoice = async (tutor) => {
    try {
      const response = await adminApi.getTutorInvoice(
        tutor._id,
        selectedMonth,
        selectedYear
      );
      
      // Extract the actual invoice data from the nested response
      const actualInvoiceData = response.data.data;
      
      setInvoiceData(actualInvoiceData);
      setSelectedRecipient(tutor);
      setRecipientType('tutor');
    } catch (err) {
      console.error('Failed to load invoice:', err);
      setError(err.response?.data?.message || 'Failed to load invoice');
    }
  };

  // Email invoice handler removed — invoices are not sent via email.

  const handleMarkAsPaid = async (tutor) => {
    const confirmed = window.confirm(
      `Mark invoice as paid for ${tutor.name}?\n\n` +
      `This will notify the tutor that their payment has been transferred.`
    );

    if (!confirmed) return;

    // Prompt for optional notes
    const notes = window.prompt(
      `Optional payment notes for ${tutor.name}:`,
      ''
    );

    // User cancelled the prompt
    if (notes === null) return;

    try {
      const response = await adminApi.markInvoiceAsPaid(
        tutor._id,
        selectedMonth,
        selectedYear,
        notes
      );
      
      alert(`Invoice marked as paid for ${tutor.name}. Tutor has been notified.`);
      refreshEndorsementData();
    } catch (err) {
      console.error('Failed to mark invoice as paid:', err);
      alert(err.response?.data?.message || 'Failed to mark invoice as paid');
    }
  };

  const handleRevertToPending = async (tutor) => {
    const reason = window.prompt(
      `Revert ${tutor.name}'s endorsement back to pending?\n\nThis will require them to review and re-endorse.\n\nPlease enter a reason:`
    );

    if (!reason) return; // User cancelled or empty reason

    try {
      await adminApi.revertToPending(
        tutor._id,
        selectedMonth,
        selectedYear,
        reason
      );
      
      alert(`Endorsement reverted to pending for ${tutor.name}. Tutor has been notified.`);
      refreshEndorsementData();
    } catch (err) {
      console.error('Failed to revert endorsement:', err);
      alert(err.response?.data?.message || 'Failed to revert endorsement');
    }
  };

  return (
    <div className="invoice-management">
      <div className="page-header with-background">
        <h1 className="page-header-title">Tutor Invoice Management</h1>
        <p className="page-header-subtitle">Generate and manage invoices for tutor payments</p>
      </div>

      {/* Tab Navigation */}
      <div className="shared-tabs">
        <button 
          className={activeTab === 'generate' ? 'active' : ''}
          onClick={() => setActiveTab('generate')}
        >
          Generate Invoice
        </button>
        <button 
          className={activeTab === 'trends' ? 'active' : ''}
          onClick={() => setActiveTab('trends')}
        >
          Earnings Trends
        </button>
        <button 
          className={activeTab === 'recurring' ? 'active' : ''}
          onClick={() => setActiveTab('recurring')}
        >
          Recurring Payments
        </button>
        <button 
          className={activeTab === 'adjustments' ? 'active' : ''}
          onClick={() => setActiveTab('adjustments')}
        >
          Payment Adjustments
        </button>
        <button 
          className={activeTab === 'audit' ? 'active' : ''}
          onClick={() => setActiveTab('audit')}
        >
          Audit Trail
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'generate' && (
          <div className="generate-invoice-tab endorsement-integrated">
            {/* Month/Year Navigator */}
            <MonthYearNavigator
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
              onMonthChange={(month) => {
                setSelectedMonth(month);
                saveMonthYear('invoice_management', month, selectedYear);
              }}
              onYearChange={(year) => {
                setSelectedYear(year);
                saveMonthYear('invoice_management', selectedMonth, year);
              }}
            />

            {/* Statistics */}
            {statistics && (
              <EndorsementStatistics statistics={statistics} />
            )}

            {/* Status Filter Buttons, Search, and Profit Sharing */}
            <div className="status-filters-row">
              <div className="status-filters">
                <button 
                  className={statusFilter === 'all' ? 'active' : ''}
                  onClick={() => setStatusFilter('all')}
                >
                  All
                </button>
                <button 
                  className={statusFilter === 'endorsed' ? 'active' : ''}
                  onClick={() => setStatusFilter('endorsed')}
                >
                  Endorsed
                </button>
                <button 
                  className={statusFilter === 'pending' ? 'active' : ''}
                  onClick={() => setStatusFilter('pending')}
                >
                  Pending
                </button>
                <button 
                  className={statusFilter === 'disputed' ? 'active' : ''}
                  onClick={() => setStatusFilter('disputed')}
                >
                  Disputed
                </button>
              </div>

              <div className="controls-right">
                <input
                  type="text"
                  className="tutor-search"
                  placeholder="Search tutors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                
                {!loading && !error && (() => {
                  const { endorsed, pending, disputed } = getGroupedTutors();
                  const totalEndorsed = endorsed.reduce((sum, tutor) => sum + (tutor.totalAmount || 0), 0);
                  const totalPending = [...pending, ...disputed].reduce((sum, tutor) => sum + (tutor.totalAmount || 0), 0);
                  const totalMoneyOut = totalEndorsed + totalPending;

                  return (
                    <>
                      {(endorsed.length > 0 || pending.length > 0 || disputed.length > 0) && (
                        <span className="total-money-out-label">
                          Total Money Out: <strong>${totalMoneyOut.toFixed(2)}</strong>
                        </span>
                      )}
                      <button 
                        className="profit-sharing-btn"
                        onClick={() => setShowProfitSharingModal(true)}
                        title="Manage profit sharing allocations for this period"
                      >
                        <svg 
                          className="profit-icon" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2"
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <circle cx="12" cy="12" r="10"/>
                          <path d="M12 6v6l4 2"/>
                        </svg>
                        Profit Sharing
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading endorsement status...</p>
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="error-state">
                <p className="error-message">{error}</p>
                <button className="retry-btn" onClick={fetchEndorsementStatus}>
                  Try Again
                </button>
              </div>
            )}

            {/* Tutor Lists - Table Style */}
            {!loading && !error && (() => {
              const { endorsed, pending, disputed } = getGroupedTutors();
              
              // Calculate total money out
              const totalEndorsed = endorsed.reduce((sum, tutor) => sum + (tutor.totalAmount || 0), 0);
              const totalPending = [...pending, ...disputed].reduce((sum, tutor) => sum + (tutor.totalAmount || 0), 0);
              const totalMoneyOut = totalEndorsed + totalPending;
              
              const formatCurrency = (amount) => {
                if (amount < 0) {
                  return `-$${Math.abs(amount).toFixed(2)}`;
                }
                return `$${amount.toFixed(2)}`;
              };

              const formatDate = (dateString) => {
                if (!dateString) return '';
                const date = new Date(dateString);
                return date.toLocaleDateString('en-SG', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                });
              };

              const isReminderRecentlySent = (tutor) => {
                if (!tutor.lastReminderSent) return false;
                const reminderDate = new Date(tutor.lastReminderSent);
                const now = new Date();
                const hoursSinceReminder = (now - reminderDate) / (1000 * 60 * 60);
                return hoursSinceReminder < 24;
              };

              const getReminderButtonTooltip = (tutor) => {
                if (!tutor.lastReminderSent) return '';
                const reminderDate = new Date(tutor.lastReminderSent);
                const now = new Date();
                const hoursSinceReminder = (now - reminderDate) / (1000 * 60 * 60);
                if (hoursSinceReminder < 24) {
                  const hoursRemaining = Math.ceil(24 - hoursSinceReminder);
                  return `Reminder sent recently. Please wait ${hoursRemaining} hour${hoursRemaining !== 1 ? 's' : ''} before sending another.`;
                }
                return '';
              };
              
              return (
                <>
                  {/* Ready for Invoice Section - Table Style */}
                  {(statusFilter === 'all' || statusFilter === 'endorsed') && (
                    <div className="invoice-table-section">
                      <div className="table-header">
                        <div className="table-header-left">
                          <h2>Ready for Invoice <span className="invoice-count">({endorsed.length})</span></h2>
                          {endorsed.length > 0 && (
                            <span className="total-amount-paid">
                              Total Amount: ${endorsed.reduce((sum, tutor) => sum + (tutor.totalAmount || 0), 0).toFixed(2)}
                            </span>
                          )}
                        </div>
                        {endorsed.length > 0 && (
                          <div className="table-header-actions">
                            <button className="bulk-action-btn" onClick={() => setShowBulkExportModal(true)}>
                              Download All PDFs
                            </button>
                            {/* Bulk Email button removed — invoices are not sent via email. */}
                          </div>
                        )}
                      </div>
                      <div className="invoice-table-container">
                        {endorsed.length === 0 ? (
                          <div className="no-invoices">
                            <p>No endorsed tutors for this period</p>
                          </div>
                        ) : (
                          <table className="invoice-table">
                            <thead>
                              <tr>
                                <th>Tutor</th>
                                <th>Lessons</th>
                                <th>Lesson Amount</th>
                                <th>Adjustments</th>
                                <th>Recurring</th>
                                <th>Total Amount</th>
                                <th>Endorsed At</th>
                                <th>Invoice</th>
                                <th>Payment</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {endorsed.map(tutor => (
                                <tr key={tutor._id}>
                                  <td>{tutor.name}</td>
                                  <td>{tutor.lessonCount}</td>
                                  <td>{formatCurrency(tutor.lessonAmount || 0)}</td>
                                  <td>
                                    <span className={tutor.adjustments > 0 ? 'amount-positive' : tutor.adjustments < 0 ? 'amount-negative' : ''}>
                                      {formatCurrency(tutor.adjustments || 0)}
                                    </span>
                                  </td>
                                  <td>
                                    {tutor.recurring !== 0 ? (
                                      <span className={tutor.recurring > 0 ? 'amount-positive' : 'amount-negative'}>
                                        {formatCurrency(tutor.recurring)}
                                      </span>
                                    ) : (
                                      <span>$0.00</span>
                                    )}
                                  </td>
                                  <td>
                                    <strong className={tutor.totalAmount < 0 ? 'amount-negative' : 'total-amount'}>
                                      {formatCurrency(tutor.totalAmount)}
                                    </strong>
                                  </td>
                                  <td>{formatDate(tutor.endorsedAt)}</td>
                                  <td>
                                    {tutor.invoiceGenerated ? (
                                      <div>
                                        <div>Generated</div>
                                        <div>{formatDate(tutor.invoiceGeneratedAt)}</div>
                                      </div>
                                    ) : (
                                      <span>Not generated</span>
                                    )}
                                  </td>
                                  <td>
                                    {tutor.paymentStatus === 'paid' && tutor.paidAt ? (
                                      <div>
                                        <div className="payment-paid">Paid</div>
                                        <div>{formatDate(tutor.paidAt)}</div>
                                      </div>
                                    ) : (
                                      <span className="payment-unpaid">Unpaid</span>
                                    )}
                                  </td>
                                  <td>
                                    <div className="action-buttons">
                                      {tutor.invoiceGenerated ? (
                                        <>
                                          <button 
                                            className="btn-icon" 
                                            onClick={() => handleViewInvoice(tutor)}
                                            title="View Invoice"
                                          >
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                              <circle cx="12" cy="12" r="3"/>
                                            </svg>
                                          </button>
                                          {/* Email Invoice button removed — invoices are not sent via email. */}
                                          {(tutor.paymentStatus === 'unpaid' || !tutor.paymentStatus) && (
                                            <button 
                                              className="btn-icon" 
                                              onClick={() => handleMarkAsPaid(tutor)}
                                              title="Mark as Paid"
                                            >
                                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <line x1="12" y1="1" x2="12" y2="23"/>
                                                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                                              </svg>
                                            </button>
                                          )}
                                          {(tutor.paymentStatus === 'unpaid' || !tutor.paymentStatus) && (
                                            <button 
                                              className="btn-icon btn-icon-warning" 
                                              onClick={() => handleRevertToPending(tutor)}
                                              title="Revert to Pending Endorsement"
                                            >
                                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="1 4 1 10 7 10"/>
                                                <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
                                              </svg>
                                            </button>
                                          )}
                                        </>
                                      ) : (
                                        <>
                                          <button 
                                            className="btn-icon" 
                                            onClick={() => handleGenerateInvoiceForTutor(tutor)}
                                            title="Generate Invoice"
                                          >
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                              <polyline points="14 2 14 8 20 8"/>
                                              <line x1="12" y1="18" x2="12" y2="12"/>
                                              <line x1="9" y1="15" x2="15" y2="15"/>
                                            </svg>
                                          </button>
                                          {(tutor.paymentStatus === 'unpaid' || !tutor.paymentStatus) && (
                                            <button 
                                              className="btn-icon" 
                                              onClick={() => handleMarkAsPaid(tutor)}
                                              title="Mark as Paid (no invoice)"
                                            >
                                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <line x1="12" y1="1" x2="12" y2="23"/>
                                                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                                              </svg>
                                            </button>
                                          )}
                                          {(tutor.paymentStatus === 'unpaid' || !tutor.paymentStatus) && (
                                            <button 
                                              className="btn-icon btn-icon-warning" 
                                              onClick={() => handleRevertToPending(tutor)}
                                              title="Revert to Pending Endorsement"
                                            >
                                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="1 4 1 10 7 10"/>
                                                <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
                                              </svg>
                                            </button>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Pending Endorsement Section - Table Style */}
                  {(statusFilter === 'all' || statusFilter === 'pending' || statusFilter === 'disputed') && (
                    <div className="invoice-table-section" style={{marginTop: '30px'}}>
                      <div className="table-header">
                        <div className="table-header-left">
                          <h2>Pending Endorsement <span className="invoice-count">({pending.length + disputed.length})</span></h2>
                          {(pending.length > 0 || disputed.length > 0) && (
                            <span className="total-amount-pending">
                              Total Amount: ${[...pending, ...disputed].reduce((sum, tutor) => sum + (tutor.totalAmount || 0), 0).toFixed(2)}
                            </span>
                          )}
                        </div>
                        {pending.length > 0 && (
                          <button 
                            className="btn-send-reminder-all"
                            onClick={handleSendReminderToAll}
                            disabled={loading}
                          >
                            Send Reminder to All Pending
                          </button>
                        )}
                      </div>
                      <div className="invoice-table-container">
                        {pending.length === 0 && disputed.length === 0 ? (
                          <div className="no-invoices">
                            <p>No pending endorsements</p>
                          </div>
                        ) : (
                          <table className="invoice-table">
                            <thead>
                              <tr>
                                <th>Tutor</th>
                                <th>Lessons</th>
                                <th>Lesson Amount</th>
                                <th>Adjustments</th>
                                <th>Recurring</th>
                                <th>Total Amount</th>
                                <th>Status</th>
                                <th>Days Overdue</th>
                                <th>Last Reminder</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(statusFilter === 'all' || statusFilter === 'pending') && pending.map(tutor => (
                                <tr key={tutor._id}>
                                  <td>{tutor.name}</td>
                                  <td>{tutor.lessonCount}</td>
                                  <td>{formatCurrency(tutor.lessonAmount || 0)}</td>
                                  <td>
                                    <span className={tutor.adjustments > 0 ? 'amount-positive' : tutor.adjustments < 0 ? 'amount-negative' : ''}>
                                      {formatCurrency(tutor.adjustments || 0)}
                                    </span>
                                  </td>
                                  <td>
                                    {tutor.recurring !== 0 ? (
                                      <span className={tutor.recurring > 0 ? 'amount-positive' : 'amount-negative'}>
                                        {formatCurrency(tutor.recurring)}
                                      </span>
                                    ) : (
                                      <span>$0.00</span>
                                    )}
                                  </td>
                                  <td>
                                    <strong className={tutor.totalAmount < 0 ? 'amount-negative' : 'total-amount'}>
                                      {formatCurrency(tutor.totalAmount)}
                                    </strong>
                                  </td>
                                  <td>
                                    <span className="status-badge status-pending">Pending</span>
                                  </td>
                                  <td>
                                    {tutor.daysSinceMonthEnd !== undefined ? (
                                      <span>{tutor.daysSinceMonthEnd} days</span>
                                    ) : (
                                      <span>-</span>
                                    )}
                                  </td>
                                  <td>
                                    {tutor.lastReminderSent ? (
                                      <div>{formatDate(tutor.lastReminderSent)}</div>
                                    ) : (
                                      <span>Not sent</span>
                                    )}
                                  </td>
                                  <td>
                                    <div className="action-buttons">
                                      <button 
                                        className="btn-icon" 
                                        onClick={() => handleSendReminder(tutor)}
                                        disabled={isReminderRecentlySent(tutor)}
                                        title={getReminderButtonTooltip(tutor) || "Send Reminder"}
                                      >
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                                          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                                        </svg>
                                      </button>
                                      <button 
                                        className="btn-icon" 
                                        onClick={() => handleViewInvoice(tutor)}
                                        title="View Invoice"
                                      >
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                          <polyline points="14 2 14 8 20 8"/>
                                          <line x1="16" y1="13" x2="8" y2="13"/>
                                          <line x1="16" y1="17" x2="8" y2="17"/>
                                          <polyline points="10 9 9 9 8 9"/>
                                        </svg>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                              {(statusFilter === 'all' || statusFilter === 'disputed') && disputed.map(tutor => (
                                <tr key={tutor._id}>
                                  <td>{tutor.name}</td>
                                  <td>{tutor.lessonCount}</td>
                                  <td>{formatCurrency(tutor.lessonAmount || 0)}</td>
                                  <td>
                                    <span className={tutor.adjustments > 0 ? 'amount-positive' : tutor.adjustments < 0 ? 'amount-negative' : ''}>
                                      {formatCurrency(tutor.adjustments || 0)}
                                    </span>
                                  </td>
                                  <td>
                                    {tutor.recurring !== 0 ? (
                                      <span className={tutor.recurring > 0 ? 'amount-positive' : 'amount-negative'}>
                                        {formatCurrency(tutor.recurring)}
                                      </span>
                                    ) : (
                                      <span>$0.00</span>
                                    )}
                                  </td>
                                  <td>
                                    <strong className={tutor.totalAmount < 0 ? 'amount-negative' : 'total-amount'}>
                                      {formatCurrency(tutor.totalAmount)}
                                    </strong>
                                  </td>
                                  <td>
                                    <span className="status-badge status-disputed">Disputed ({tutor.disputeCount})</span>
                                  </td>
                                  <td>
                                    {tutor.daysSinceMonthEnd !== undefined ? (
                                      <span>{tutor.daysSinceMonthEnd} days</span>
                                    ) : (
                                      <span>-</span>
                                    )}
                                  </td>
                                  <td>
                                    {tutor.lastReminderSent ? (
                                      <div>{formatDate(tutor.lastReminderSent)}</div>
                                    ) : (
                                      <span>Not sent</span>
                                    )}
                                  </td>
                                  <td>
                                    <div className="action-buttons">
                                      <button 
                                        className="btn-icon" 
                                        onClick={() => handleViewDisputes(tutor)}
                                        title={`Resolve Disputes (${tutor.disputeCount})`}
                                      >
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <circle cx="12" cy="12" r="10"/>
                                          <line x1="15" y1="9" x2="9" y2="15"/>
                                          <line x1="9" y1="9" x2="15" y2="15"/>
                                        </svg>
                                      </button>
                                      <button 
                                        className="btn-icon" 
                                        onClick={() => handleViewInvoice(tutor)}
                                        title="View Invoice"
                                      >
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                          <polyline points="14 2 14 8 20 8"/>
                                          <line x1="16" y1="13" x2="8" y2="13"/>
                                          <line x1="16" y1="17" x2="8" y2="17"/>
                                          <polyline points="10 9 9 9 8 9"/>
                                        </svg>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

            {/* Invoice Preview (shown when invoice is generated) */}
            {invoiceData && selectedRecipient && (
              <div className="invoice-preview-overlay">
                <div className="invoice-preview-modal">
                  <div className="invoice-preview-header">
                    <h3>Invoice Preview</h3>
                    <button 
                      className="close-preview" 
                      onClick={() => {
                        setInvoiceData(null);
                        setSelectedRecipient(null);
                      }}
                      aria-label="Close invoice preview"
                    >
                      ×
                    </button>
                  </div>
                  <div className="invoice-preview-content">
                    <InvoicePreview
                      invoiceType={recipientType}
                      recipient={selectedRecipient}
                      month={selectedMonth}
                      year={selectedYear}
                      invoiceData={invoiceData}
                      onClose={() => {
                        setInvoiceData(null);
                        setSelectedRecipient(null);
                        refreshEndorsementData();
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'generate' && (
          <>
            <BulkExportModal
              isOpen={showBulkExportModal}
              onClose={() => setShowBulkExportModal(false)}
              tutors={tutors}
              tutees={tutees}
              endorsedTutors={getGroupedTutors().endorsed}
              month={selectedMonth}
              year={selectedYear}
              endorsementContext={true}
              onBulkComplete={refreshEndorsementData}
            />
            {/* BulkEmailModal removed — invoices are not sent via email. */}
            {showDetailModal && selectedTutor && (
              <TutorDetailModal
                tutor={selectedTutor}
                month={selectedMonth}
                year={selectedYear}
                onClose={() => {
                  setShowDetailModal(false);
                  setSelectedTutor(null);
                }}
                onGenerateInvoice={handleGenerateInvoiceForTutor}
              />
            )}
            {showDisputeModal && selectedTutor && (
              <DisputeResolutionModal
                isOpen={showDisputeModal}
                tutor={selectedTutor}
                month={selectedMonth}
                year={selectedYear}
                onClose={() => {
                  setShowDisputeModal(false);
                  setSelectedTutor(null);
                }}
                onDisputeResolved={handleDisputeResolved}
              />
            )}
            <ProfitSharingModal
              isOpen={showProfitSharingModal}
              month={selectedMonth}
              year={selectedYear}
              onClose={() => setShowProfitSharingModal(false)}
              onSave={() => {
                refreshEndorsementData();
              }}
            />
          </>
        )}

        {activeTab === 'trends' && (
          <div className="earnings-trends-tab">
            <TutorEarningsTrends
              onViewAllTutorsForMonth={(month, year) => {
                setSelectedMonth(month);
                setSelectedYear(year);
                saveMonthYear('invoice_management', month, year);
                setActiveTab('generate');
              }}
            />
          </div>
        )}

        {activeTab === 'recurring' && (
          <div className="recurring-payments-tab">
            <RecurringPaymentManager tutors={tutors} onPaymentChanged={refreshEndorsementData} />
          </div>
        )}

        {activeTab === 'adjustments' && (
          <div className="payment-adjustments-tab">
            <PaymentAdjustmentsList tutors={tutors} />
          </div>
        )}

        {activeTab === 'audit' && (
          <div className="audit-trail-tab">
            <AuditTrail />
          </div>
        )}
      </div>

      {/* Profit Sharing Modal */}
      {showProfitSharingModal && (
        <ProfitSharingModal
          isOpen={showProfitSharingModal}
          onClose={() => setShowProfitSharingModal(false)}
          profitSharers={tutors.filter(t => t.isCoFounder && t.isActive)}
          mode="monthly"
          initialMonth={selectedMonth}
          initialYear={selectedYear}
        />
      )}
    </div>
  );
};

export default InvoiceManagement;
